import { ExpandLess, ExpandMore, Mail as MailIcon } from '@mui/icons-material';
import { Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { postOrganizationAdvocacyAnalyticsReportAction } from 'store/actions/advocacyReports';
import { setSnackbarError, setSnackbarSuccess } from 'store/actions/global';
import {
  BuildButton,
  DatesWrapper,
  OptionsSection,
  OptionsToggle,
  ReportDescription,
  ReportTitle,
  StyledCard,
} from '../styles';

const OrganizationAdvocacyReport = ({ organizationUuid }) => {
  const dispatch = useDispatch();

  const [showOptions, setShowOptions] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [includeActions, setIncludeActions] = useState(false);
  const [includeSignups, setIncludeSignups] = useState(false);

  const handleOptionsToggle = () => setShowOptions(prevState => !prevState);

  const handleBuildReport = () => {
    const payload = {};

    if (startDate) {
      payload.start_date = dayjs(startDate).format('YYYY-MM-DD');
    }

    if (endDate) {
      payload.end_date = dayjs(endDate).format('YYYY-MM-DD');
    }

    if (includeActions) {
      payload.include_actions = true;
    }

    if (includeSignups) {
      payload.include_signups = true;
    }

    dispatch(postOrganizationAdvocacyAnalyticsReportAction(payload, organizationUuid))
      .then(result => {
        dispatch(setSnackbarSuccess({ message: result?.detail }));
      })
      .catch(() => {
        dispatch(
          setSnackbarError({
            message: 'Failed to build report. Make sure all fields are set properly',
          })
        );
      });
  };

  return (
    <StyledCard variant="outlined">
      <ReportTitle variant="h6">Total Advocacy Report</ReportTitle>
      <ReportDescription variant="body2">
        <p>Excel report spanning all campaigns and action centers in your organization.</p>
        <p>
          <strong>Customer input requested!</strong>
          &nbsp;Use our support chat to share what kind of cross-campaign data would be helpful to
          understand and share your advocacy efforts.
        </p>
      </ReportDescription>

      <OptionsToggle onClick={handleOptionsToggle}>
        {showOptions ? 'Hide options' : 'Options'}
        {showOptions ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </OptionsToggle>

      {showOptions && (
        <OptionsSection>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={includeSignups}
                onChange={e => setIncludeSignups(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Include signups worksheet</Typography>}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={includeActions}
                onChange={e => setIncludeActions(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Include actions worksheet</Typography>}
          />

          <DatesWrapper>
            <TextField
              label="Start date"
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
            <TextField
              label="End date"
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </DatesWrapper>
        </OptionsSection>
      )}

      <BuildButton
        variant="contained"
        color="primary"
        startIcon={<MailIcon />}
        onClick={handleBuildReport}
      >
        Build Report
      </BuildButton>
    </StyledCard>
  );
};

OrganizationAdvocacyReport.propTypes = {
  organizationUuid: PropTypes.string.isRequired,
};

export default OrganizationAdvocacyReport;
